/**
 * TestIds - Centralized constants for test IDs used across the application
 * 
 * This file provides a structured set of test IDs organized by page and component
 * to be used in both component rendering and Playwright tests. Using these constants
 * ensures consistency and prevents mismatches between component implementation and tests.
 */

/** General elements that appear across multiple pages */
export const COMMON = {
  NAVBAR: {
    CONTAINER: 'navbar-container',
    LOGO: 'navbar-logo',
    MENU_BUTTON: 'navbar-menu-button',
    USER_DROPDOWN: 'navbar-user-dropdown',
    USER_DROPDOWN_MENU: {
      TRIGGER: 'user-dropdown-trigger',
      CONTENT: 'user-dropdown-content',
      PROFILE_LINK: 'user-dropdown-profile-link',
      PAYMENT_PIN_LINK: 'user-dropdown-payment-pin-link',
      SETTINGS_LINK: 'user-dropdown-settings-link',
      LOGOUT_BUTTON: 'user-dropdown-logout-button',
    },
  },
  SIDEBAR: {
    CONTAINER: 'sidebar-container',
    NAV_ITEM: (label: string) => `sidebar-nav-item-${label}`,
    THEME_SWITCHER: 'sidebar-theme-switcher',
    CURRENCY_SELECTOR: 'sidebar-currency-selector',
  },
  SEARCH_BAR: {
    CONTAINER: 'search-bar-container',
    INPUT: 'search-bar-input',
    CLEAR_BUTTON: 'search-bar-clear-button',
  },
  PAGINATION: {
    CONTAINER: 'pagination-container',
    PREV_BUTTON: 'pagination-prev-button',
    NEXT_BUTTON: 'pagination-next-button',
    PAGE_BUTTON: (page: number) => `pagination-page-button-${page}`,
  },
  DIALOG: {
    CONTAINER: (name: string) => `${name}-dialog-container`,
    TITLE: (name: string) => `${name}-dialog-title`,
    CLOSE_BUTTON: (name: string) => `${name}-dialog-close-button`,
    CONTENT: (name: string) => `${name}-dialog-content`,
    CONFIRM_BUTTON: (name: string) => `${name}-dialog-confirm-button`,
    CANCEL_BUTTON: (name: string) => `${name}-dialog-cancel-button`,
  },
  ALERTS: {
    SUCCESS: 'alert-success',
    ERROR: 'alert-error',
    WARNING: 'alert-warning',
    INFO: 'alert-info',
  },
  OFFLINE_BANNER: 'offline-banner',
  SYNC_INDICATOR: 'sync-indicator'
};

/** Login and Authentication related components */
export const AUTH = {
  LOGIN_FORM: {
    CONTAINER: 'login-form-container',
    EMAIL_INPUT: 'login-email-input',
    PASSWORD_INPUT: 'login-password-input',
    SUBMIT_BUTTON: 'login-submit-button',
    ERROR_MESSAGE: 'login-error-message',
    FORGOT_PASSWORD: 'login-forgot-password-link',
  },
  REGISTER_FORM: {
    CONTAINER: 'register-form-container',
    NAME_INPUT: 'register-name-input',
    EMAIL_INPUT: 'register-email-input',
    PASSWORD_INPUT: 'register-password-input',
    CONFIRM_PASSWORD_INPUT: 'register-confirm-password-input',
    SUBMIT_BUTTON: 'register-submit-button',
    ERROR_MESSAGE: 'register-error-message',
  },
  PIN_MANAGEMENT: {
    CONTAINER: 'pin-management-container',
    PIN_INPUT: 'pin-input',
    SUBMIT_BUTTON: 'pin-submit-button',
    ERROR_MESSAGE: 'pin-error-message',
  }
};

/** Dashboard components */
export const DASHBOARD = {
  CONTAINER: 'dashboard-container',
  OVERVIEW: {
    CONTAINER: 'dashboard-overview-container',
    HEADING: 'dashboard-overview-heading',
    STATS_CARD: (label: string) => `dashboard-stats-card-${label}`,
  },
  RECENT_SALES: {
    CONTAINER: 'dashboard-recent-sales-container',
    HEADING: 'dashboard-recent-sales-heading',
    SALE_ITEM: (id: string) => `dashboard-sale-item-${id}`,
    VIEW_ALL: 'dashboard-recent-sales-view-all',
  },
  QUICK_ACTIONS: {
    CONTAINER: 'dashboard-quick-actions-container',
    ACTION_BUTTON: (action: string) => `dashboard-action-${action}`,
  }
};

/** Sales page and components */
export const SALES = {
  CONTAINER: 'sales-container',
  LIST: {
    CONTAINER: 'sales-list-container',
    FILTER_SECTION: 'sales-filter-section',
    FILTER: {
      STATUS: 'sales-filter-status',
      DATE_RANGE: 'sales-filter-date-range',
      PAYMENT_METHOD: 'sales-filter-payment-method',
      CLEAR_BUTTON: 'sales-filter-clear-button',
      APPLY_BUTTON: 'sales-filter-apply-button',
    },
    SEARCH: 'sales-search-input',
    TABLE: {
      CONTAINER: 'sales-table-container',
      HEADER_ROW: 'sales-table-header-row',
      ROW: (id: string) => `sales-table-row-${id}`,
      CELL: {
        ID: (id: string) => `sales-table-cell-id-${id}`,
        PATIENT: (id: string) => `sales-table-cell-patient-${id}`,
        DATE: (id: string) => `sales-table-cell-date-${id}`,
        TOTAL: (id: string) => `sales-table-cell-total-${id}`,
        STATUS: (id: string) => `sales-table-cell-status-${id}`,
        ACTIONS: (id: string) => `sales-table-cell-actions-${id}`,
      }
    },
    NO_RESULTS: 'sales-no-results'
  },
  CREATE_PAGE: {
    CONTAINER: 'sale-create-page-container',
    BACK_BUTTON: 'sale-create-page-back-button'
  },
  FORM: {
    CONTAINER: 'sale-form-container',
    PATIENT_SEARCH: 'sale-form-patient-search',
    PHARMACY_SELECT: 'sale-form-pharmacy-select',
    CHECK_REFILLS_BUTTON: 'sale-form-check-refills-button',
    MEDICINE_SEARCH: 'sale-form-medicine-search',
    
    ITEMS: {
      CONTAINER: 'sale-form-items-container',
      ITEM: (index: number) => `sale-form-item-${index}`,
      NAME: (index: number) => `sale-form-item-name-${index}`,
      QUANTITY: (index: number) => `sale-form-item-quantity-${index}`,
      PRICE: (index: number) => `sale-form-item-price-${index}`,
      TOTAL: (index: number) => `sale-form-item-total-${index}`,
      REMOVE_BUTTON: (index: number) => `sale-form-item-remove-${index}`,
    },
    
    PRESCRIPTION_SECTION: {
      CONTAINER: 'sale-form-prescription-section',
      DOCTOR_SEARCH: 'sale-form-doctor-search',
      HOSPITAL_SEARCH: 'sale-form-hospital-search',
      REFILLS_AUTHORIZED: 'sale-form-refills-authorized',
      DAYS_BETWEEN_REFILLS: 'sale-form-days-between-refills',
      REFILL_VALIDITY: 'sale-form-refill-validity',
      PRESCRIPTION_NOTES: 'sale-form-prescription-notes',
    },
    
    PAYMENT_METHOD: 'sale-form-payment-method',
    TOTAL_AMOUNT: 'sale-form-total-amount',
    NOTES: 'sale-form-notes',
    
    BUTTONS: {
      SAVE_DRAFT: 'sale-form-save-draft-button',
      DISCARD_DRAFT: 'sale-form-discard-draft-button',
      SUBMIT: 'sale-form-submit-button',
    },
    
    DIALOGS: {
      REFILLABLE_PRESCRIPTIONS: 'refillable-prescriptions-dialog',
      PRICE_OVERRIDE: 'price-override-dialog',
      DRAFT_RESTORE: 'draft-restore-dialog',
    }
  },
  DETAILS: {
    CONTAINER: (id: string) => `sale-details-container-${id}`,
    HEADER: {
      CONTAINER: 'sale-details-header',
      ID: 'sale-details-id',
      DATE: 'sale-details-date',
      STATUS: 'sale-details-status',
    },
    PATIENT_INFO: {
      CONTAINER: 'sale-details-patient-info',
      NAME: 'sale-details-patient-name',
      CONTACT: 'sale-details-patient-contact',
    },
    ITEMS: {
      CONTAINER: 'sale-details-items',
      ITEM: (index: number) => `sale-details-item-${index}`,
    },
    PRESCRIPTION_INFO: {
      CONTAINER: 'sale-details-prescription-info',
      ID: 'sale-details-prescription-id',
      DOCTOR: 'sale-details-prescription-doctor',
      HOSPITAL: 'sale-details-prescription-hospital',
      NOTES: 'sale-details-prescription-notes',
    },
    PAYMENT_INFO: {
      CONTAINER: 'sale-details-payment-info',
      METHOD: 'sale-details-payment-method',
      TOTAL: 'sale-details-payment-total',
    },
    ACTIONS: {
      CONTAINER: 'sale-details-actions',
      EDIT: 'sale-details-edit-button',
      PRINT_RECEIPT: 'sale-details-print-receipt-button',
      PRINT_LABELS: 'sale-details-print-labels-button',
      MARK_PAID: 'sale-details-mark-paid-button',
      MARK_COMPLETED: 'sale-details-mark-completed-button',
      VOID: 'sale-details-void-button',
      CANCEL: 'sale-details-cancel-button',
    }
  },
  CARD: {
    CONTAINER: (id: string) => `sale-card-container-${id}`,
    HEADER: {
      CONTAINER: 'sale-card-header',
      ID: 'sale-card-id',
      STATUS: 'sale-card-status',
      ACTIONS_MENU: 'sale-card-actions-menu',
    },
    ITEMS: {
      CONTAINER: 'sale-card-items',
      ITEM: (index: number) => `sale-card-item-${index}`,
    },
    PRESCRIPTION_STATUS: 'sale-card-prescription-status',
    ACTION_BUTTONS: {
      CONTAINER: 'sale-card-action-buttons',
      PAY: 'sale-card-pay-button',
      COMPLETE: 'sale-card-complete-button',
    },
    FOOTER: {
      CONTAINER: 'sale-card-footer',
      DATE: 'sale-card-footer-date',
      PAYMENT_METHOD: 'sale-card-footer-payment-method',
      TOTAL: 'sale-card-footer-total',
    }
  }
};

/** Patients page and components */
export const PATIENTS = {
  CONTAINER: 'patients-container',
  LIST: {
    CONTAINER: 'patients-list-container',
    SEARCH: 'patients-search-input',
    TABLE: {
      CONTAINER: 'patients-table-container',
      HEADER_ROW: 'patients-table-header-row',
      ROW: (id: string) => `patients-table-row-${id}`,
      CELL: {
        NAME: (id: string) => `patients-table-cell-name-${id}`,
        ID: (id: string) => `patients-table-cell-id-${id}`,
        PHONE: (id: string) => `patients-table-cell-phone-${id}`,
        EMAIL: (id: string) => `patients-table-cell-email-${id}`,
        ACTIONS: (id: string) => `patients-table-cell-actions-${id}`,
      }
    },
    ADD_BUTTON: 'patients-add-button',
    NO_RESULTS: 'patients-no-results'
  },
  CREATE_PAGE: {
    CONTAINER: 'patient-create-page-container',
    BACK_BUTTON: 'patient-create-page-back-button'
  },
  FORM: {
    CONTAINER: 'patient-form-container',
    NAME_INPUT: 'patient-form-name-input',
    PHONE_INPUT: 'patient-form-phone-input',
    EMAIL_INPUT: 'patient-form-email-input',
    GENDER_SELECT: 'patient-form-gender-select',
    DOB_INPUT: 'patient-form-dob-input',
    ADDRESS_INPUT: 'patient-form-address-input',
    EMERGENCY_CONTACT_INPUT: 'patient-form-emergency-contact-input',
    ALLERGIES_INPUT: 'patient-form-allergies-input',
    MEDICAL_CONDITIONS_INPUT: 'patient-form-medical-conditions-input',
    SUBMIT_BUTTON: 'patient-form-submit-button',
    CANCEL_BUTTON: 'patient-form-cancel-button',
  },
  DETAILS: {
    CONTAINER: (id: string) => `patient-details-container-${id}`,
    INFO_SECTION: {
      CONTAINER: 'patient-details-info-section',
      NAME: 'patient-details-name',
      ID: 'patient-details-id',
      CONTACT: 'patient-details-contact',
      DEMOGRAPHIC: 'patient-details-demographic',
    },
    MEDICAL_SECTION: {
      CONTAINER: 'patient-details-medical-section',
      ALLERGIES: 'patient-details-allergies',
      CONDITIONS: 'patient-details-conditions',
    },
    PRESCRIPTION_SECTION: {
      CONTAINER: 'patient-details-prescription-section',
      TABLE: 'patient-details-prescription-table',
      ITEM: (id: string) => `patient-details-prescription-${id}`,
    },
    SALES_SECTION: {
      CONTAINER: 'patient-details-sales-section',
      TABLE: 'patient-details-sales-table',
      ITEM: (id: string) => `patient-details-sale-${id}`,
    },
    ACTIONS: {
      EDIT: 'patient-details-edit-button',
      DELETE: 'patient-details-delete-button',
      NEW_SALE: 'patient-details-new-sale-button',
    }
  }
};

/** Inventory/Medicines page and components */
export const INVENTORY = {
  CONTAINER: 'inventory-container',
  LIST: {
    CONTAINER: 'inventory-list-container',
    SEARCH: 'inventory-search-input',
    FILTER: {
      CATEGORY: 'inventory-filter-category',
      STOCK_STATUS: 'inventory-filter-stock-status',
      PHARMACY: 'inventory-filter-pharmacy',
      CLEAR_BUTTON: 'inventory-filter-clear-button',
      APPLY_BUTTON: 'inventory-filter-apply-button',
    },
    TABLE: {
      CONTAINER: 'inventory-table-container',
      HEADER_ROW: 'inventory-table-header-row',
      ROW: (id: string) => `inventory-table-row-${id}`,
      CELL: {
        NAME: (id: string) => `inventory-table-cell-name-${id}`,
        CATEGORY: (id: string) => `inventory-table-cell-category-${id}`,
        STOCK: (id: string) => `inventory-table-cell-stock-${id}`,
        PRICE: (id: string) => `inventory-table-cell-price-${id}`,
        ACTIONS: (id: string) => `inventory-table-cell-actions-${id}`,
      }
    },
    ADD_BUTTON: 'inventory-add-button',
    NO_RESULTS: 'inventory-no-results'
  },
  FORM: {
    CONTAINER: 'medicine-form-container',
    NAME_INPUT: 'medicine-form-name-input',
    CATEGORY_SELECT: 'medicine-form-category-select',
    DESCRIPTION_INPUT: 'medicine-form-description-input',
    MANUFACTURER_INPUT: 'medicine-form-manufacturer-input',
    
    VARIANTS: {
      CONTAINER: 'medicine-form-variants-container',
      VARIANT: (index: number) => `medicine-form-variant-${index}`,
      STRENGTH: (index: number) => `medicine-form-variant-strength-${index}`,
      DOSAGE_FORM: (index: number) => `medicine-form-variant-dosage-form-${index}`,
      UNIT_PRICE: (index: number) => `medicine-form-variant-unit-price-${index}`,
      UNIT_COST: (index: number) => `medicine-form-variant-unit-cost-${index}`,
      REQUIRES_PRESCRIPTION: (index: number) => `medicine-form-variant-requires-prescription-${index}`,
      REMOVE_BUTTON: (index: number) => `medicine-form-variant-remove-${index}`,
    },
    
    ADD_VARIANT_BUTTON: 'medicine-form-add-variant-button',
    SUBMIT_BUTTON: 'medicine-form-submit-button',
    CANCEL_BUTTON: 'medicine-form-cancel-button',
  },
  DETAILS: {
    CONTAINER: (id: string) => `medicine-details-container-${id}`,
    BASIC_INFO: {
      CONTAINER: 'medicine-details-basic-info',
      NAME: 'medicine-details-name',
      CATEGORY: 'medicine-details-category',
      DESCRIPTION: 'medicine-details-description',
      MANUFACTURER: 'medicine-details-manufacturer',
    },
    VARIANTS: {
      CONTAINER: 'medicine-details-variants',
      VARIANT: (id: string) => `medicine-details-variant-${id}`,
    },
    STOCK_LEVELS: {
      CONTAINER: 'medicine-details-stock-levels',
      PHARMACY: (id: string) => `medicine-details-stock-pharmacy-${id}`,
    },
    ACTIONS: {
      EDIT: 'medicine-details-edit-button',
      DELETE: 'medicine-details-delete-button',
      ADJUST_STOCK: 'medicine-details-adjust-stock-button',
    }
  },
  STOCK_ADJUSTMENT: {
    CONTAINER: 'stock-adjustment-container',
    MEDICINE_SELECT: 'stock-adjustment-medicine-select',
    VARIANT_SELECT: 'stock-adjustment-variant-select',
    PHARMACY_SELECT: 'stock-adjustment-pharmacy-select',
    QUANTITY_INPUT: 'stock-adjustment-quantity-input',
    TYPE_SELECT: 'stock-adjustment-type-select',
    REASON_INPUT: 'stock-adjustment-reason-input',
    DESTINATION_PHARMACY_SELECT: 'stock-adjustment-destination-pharmacy-select',
    NOTES_INPUT: 'stock-adjustment-notes-input',
    QUANTITY_ERROR: 'stock-adjustment-quantity-error',
    SUBMIT_BUTTON: 'stock-adjustment-submit-button',
    CANCEL_BUTTON: 'stock-adjustment-cancel-button',
  },
  LIST_FILTERS: {
    FILTER: {
      CONTAINER: 'inventory-filter-container',
      CATEGORY: 'inventory-filter-category',
      STOCK_STATUS: 'inventory-filter-stock-status',
      PHARMACY: 'inventory-filter-pharmacy',
      CLEAR_BUTTON: 'inventory-filter-clear-button',
      APPLY_BUTTON: 'inventory-filter-apply-button',
      TOGGLE_BUTTON: 'inventory-filter-toggle-button',
    }
  },
  CREATE_PAGE: {
    CONTAINER: 'medicine-create-page-container',
    BACK_BUTTON: 'medicine-create-page-back-button'
  }
};

/** Pharmacy management components */
export const PHARMACY = {
  CONTAINER: 'pharmacy-container',
  LIST: {
    CONTAINER: 'pharmacy-list-container',
    SEARCH: 'pharmacy-search-input',
    TABLE: {
      CONTAINER: 'pharmacy-table-container',
      HEADER_ROW: 'pharmacy-table-header-row',
      ROW: (id: string) => `pharmacy-table-row-${id}`,
      CELL: {
        NAME: (id: string) => `pharmacy-table-cell-name-${id}`,
        ADDRESS: (id: string) => `pharmacy-table-cell-address-${id}`,
        PHONE: (id: string) => `pharmacy-table-cell-phone-${id}`,
        LICENSE: (id: string) => `pharmacy-table-cell-license-${id}`,
        ACTIONS: (id: string) => `pharmacy-table-cell-actions-${id}`,
      }
    },
    ADD_BUTTON: 'pharmacy-add-button',
    NO_RESULTS: 'pharmacy-no-results'
  },
  FORM: {
    CONTAINER: 'pharmacy-form-container',
    NAME_INPUT: 'pharmacy-form-name-input',
    LICENSE_INPUT: 'pharmacy-form-license-input',
    ADDRESS_INPUT: 'pharmacy-form-address-input',
    PHONE_INPUT: 'pharmacy-form-phone-input',
    EMAIL_INPUT: 'pharmacy-form-email-input',
    SUBMIT_BUTTON: 'pharmacy-form-submit-button',
    CANCEL_BUTTON: 'pharmacy-form-cancel-button',
  },
  DETAILS: {
    CONTAINER: (id: string) => `pharmacy-details-container-${id}`,
    INFO_SECTION: {
      CONTAINER: 'pharmacy-details-info-section',
      NAME: 'pharmacy-details-name',
      LICENSE: 'pharmacy-details-license',
      ADDRESS: 'pharmacy-details-address',
      CONTACT: 'pharmacy-details-contact',
    },
    EMPLOYEES_SECTION: {
      CONTAINER: 'pharmacy-details-employees-section',
      TABLE: 'pharmacy-details-employees-table',
      EMPLOYEE: (id: string) => `pharmacy-details-employee-${id}`,
    },
    INVENTORY_SECTION: {
      CONTAINER: 'pharmacy-details-inventory-section',
      TABLE: 'pharmacy-details-inventory-table',
      ITEM: (id: string) => `pharmacy-details-inventory-item-${id}`,
    },
    ACTIONS: {
      EDIT: 'pharmacy-details-edit-button',
      DELETE: 'pharmacy-details-delete-button',
      ADD_EMPLOYEE: 'pharmacy-details-add-employee-button',
    }
  }
};

/** Employee management components */
export const EMPLOYEES = {
  CONTAINER: 'employees-container',
  LIST: {
    CONTAINER: 'employees-list-container',
    SEARCH: 'employees-search-input',
    FILTER: {
      ROLE: 'employees-filter-role',
      PHARMACY: 'employees-filter-pharmacy',
      STATUS: 'employees-filter-status',
      CLEAR_BUTTON: 'employees-filter-clear-button',
      APPLY_BUTTON: 'employees-filter-apply-button',
    },
    TABLE: {
      CONTAINER: 'employees-table-container',
      HEADER_ROW: 'employees-table-header-row',
      ROW: (id: string) => `employees-table-row-${id}`,
      CELL: {
        NAME: (id: string) => `employees-table-cell-name-${id}`,
        ROLE: (id: string) => `employees-table-cell-role-${id}`,
        PHARMACY: (id: string) => `employees-table-cell-pharmacy-${id}`,
        STATUS: (id: string) => `employees-table-cell-status-${id}`,
        ACTIONS: (id: string) => `employees-table-cell-actions-${id}`,
      }
    },
    ADD_BUTTON: 'employees-add-button',
    NO_RESULTS: 'employees-no-results'
  },
  FORM: {
    CONTAINER: 'employee-form-container',
    NAME_INPUT: 'employee-form-name-input',
    EMAIL_INPUT: 'employee-form-email-input',
    PHONE_INPUT: 'employee-form-phone-input',
    ROLE_SELECT: 'employee-form-role-select',
    PHARMACY_SELECT: 'employee-form-pharmacy-select',
    STATUS_SELECT: 'employee-form-status-select',
    SUBMIT_BUTTON: 'employee-form-submit-button',
    CANCEL_BUTTON: 'employee-form-cancel-button',
  },
  DETAILS: {
    CONTAINER: (id: string) => `employee-details-container-${id}`,
    INFO_SECTION: {
      CONTAINER: 'employee-details-info-section',
      NAME: 'employee-details-name',
      ROLE: 'employee-details-role',
      CONTACT: 'employee-details-contact',
      PHARMACY: 'employee-details-pharmacy',
      STATUS: 'employee-details-status',
    },
    ACTIVITY_SECTION: {
      CONTAINER: 'employee-details-activity-section',
      TABLE: 'employee-details-activity-table',
      ITEM: (id: string) => `employee-details-activity-${id}`,
    },
    ACTIONS: {
      EDIT: 'employee-details-edit-button',
      DISABLE: 'employee-details-disable-button',
      ENABLE: 'employee-details-enable-button',
      RESET_PASSWORD: 'employee-details-reset-password-button',
    }
  }
};

/** Reports page and components */
export const REPORTS = {
  CONTAINER: 'reports-container',
  TABS: {
    CONTAINER: 'reports-tabs-container',
    SALES: 'reports-tab-sales',
    INVENTORY: 'reports-tab-inventory',
    PATIENTS: 'reports-tab-patients',
    PRESCRIPTIONS: 'reports-tab-prescriptions',
  },
  FILTERS: {
    CONTAINER: 'reports-filters-container',
    DATE_RANGE: 'reports-filter-date-range',
    PHARMACY: 'reports-filter-pharmacy',
    APPLY_BUTTON: 'reports-filter-apply-button',
    EXPORT_BUTTON: 'reports-filter-export-button',
  },
  SALES_REPORT: {
    CONTAINER: 'sales-report-container',
    SUMMARY: {
      CONTAINER: 'sales-report-summary',
      TOTAL_SALES: 'sales-report-total-sales',
      TOTAL_REVENUE: 'sales-report-total-revenue',
      AVERAGE_SALE: 'sales-report-average-sale',
    },
    CHART: 'sales-report-chart',
    TABLE: {
      CONTAINER: 'sales-report-table-container',
      HEADER_ROW: 'sales-report-table-header-row',
      ROW: (index: number) => `sales-report-table-row-${index}`,
    }
  },
  INVENTORY_REPORT: {
    CONTAINER: 'inventory-report-container',
    SUMMARY: {
      CONTAINER: 'inventory-report-summary',
      TOTAL_ITEMS: 'inventory-report-total-items',
      LOW_STOCK: 'inventory-report-low-stock',
      OUT_OF_STOCK: 'inventory-report-out-of-stock',
    },
    CHART: 'inventory-report-chart',
    TABLE: {
      CONTAINER: 'inventory-report-table-container',
      HEADER_ROW: 'inventory-report-table-header-row',
      ROW: (index: number) => `inventory-report-table-row-${index}`,
    }
  },
  PATIENTS_REPORT: {
    CONTAINER: 'patients-report-container',
    SUMMARY: {
      CONTAINER: 'patients-report-summary',
      TOTAL_PATIENTS: 'patients-report-total-patients',
      NEW_PATIENTS: 'patients-report-new-patients',
      RETURNING_PATIENTS: 'patients-report-returning-patients',
    },
    CHART: 'patients-report-chart',
    TABLE: {
      CONTAINER: 'patients-report-table-container',
      HEADER_ROW: 'patients-report-table-header-row',
      ROW: (index: number) => `patients-report-table-row-${index}`,
    }
  },
  PRESCRIPTIONS_REPORT: {
    CONTAINER: 'prescriptions-report-container',
    SUMMARY: {
      CONTAINER: 'prescriptions-report-summary',
      TOTAL_PRESCRIPTIONS: 'prescriptions-report-total-prescriptions',
      REFILL_PRESCRIPTIONS: 'prescriptions-report-refill-prescriptions',
      COMPLETED_PRESCRIPTIONS: 'prescriptions-report-completed-prescriptions',
    },
    CHART: 'prescriptions-report-chart',
    TABLE: {
      CONTAINER: 'prescriptions-report-table-container',
      HEADER_ROW: 'prescriptions-report-table-header-row',
      ROW: (index: number) => `prescriptions-report-table-row-${index}`,
    }
  }
};

/** Settings page and components */
export const SETTINGS = {
  CONTAINER: 'settings-container',
  TABS: {
    CONTAINER: 'settings-tabs-container',
    ACCOUNT: 'settings-tab-account',
    APPLICATION: 'settings-tab-application',
    NOTIFICATIONS: 'settings-tab-notifications',
    SECURITY: 'settings-tab-security',
  },
  ACCOUNT_SETTINGS: {
    CONTAINER: 'account-settings-container',
    NAME_INPUT: 'account-settings-name-input',
    EMAIL_INPUT: 'account-settings-email-input',
    PHONE_INPUT: 'account-settings-phone-input',
    SAVE_BUTTON: 'account-settings-save-button',
  },
  APPLICATION_SETTINGS: {
    CONTAINER: 'application-settings-container',
    LANGUAGE_SELECT: 'application-settings-language-select',
    THEME_SELECT: 'application-settings-theme-select',
    DATE_FORMAT_SELECT: 'application-settings-date-format-select',
    SAVE_BUTTON: 'application-settings-save-button',
  },
  NOTIFICATION_SETTINGS: {
    CONTAINER: 'notification-settings-container',
    EMAIL_TOGGLE: 'notification-settings-email-toggle',
    PUSH_TOGGLE: 'notification-settings-push-toggle',
    SAVE_BUTTON: 'notification-settings-save-button',
  },
  SECURITY_SETTINGS: {
    CONTAINER: 'security-settings-container',
    CURRENT_PASSWORD_INPUT: 'security-settings-current-password-input',
    NEW_PASSWORD_INPUT: 'security-settings-new-password-input',
    CONFIRM_PASSWORD_INPUT: 'security-settings-confirm-password-input',
    SAVE_BUTTON: 'security-settings-save-button',
  }
};

/** Prescriptions page and components */
export const PRESCRIPTIONS = {
  CONTAINER: 'prescriptions-container',
  LIST: {
    CONTAINER: 'prescriptions-list-container',
    SEARCH: 'prescriptions-search-input',
    FILTER: {
      STATUS: 'prescriptions-filter-status',
      DATE_RANGE: 'prescriptions-filter-date-range',
      PATIENT: 'prescriptions-filter-patient',
      DOCTOR: 'prescriptions-filter-doctor',
      HOSPITAL: 'prescriptions-filter-hospital',
      PHARMACY: 'prescriptions-filter-pharmacy',
      FROM_DATE: 'prescriptions-filter-from-date',
      TO_DATE: 'prescriptions-filter-to-date',
      CLEAR_BUTTON: 'prescriptions-filter-clear-button',
      APPLY_BUTTON: 'prescriptions-filter-apply-button',
      TOGGLE_BUTTON: 'prescriptions-filter-toggle-button',
    },
    TABLE: {
      CONTAINER: 'prescriptions-table-container',
      HEADER_ROW: 'prescriptions-table-header-row',
      ROW: (id: string) => `prescriptions-table-row-${id}`,
      CELL: {
        ID: (id: string) => `prescriptions-table-cell-id-${id}`,
        PATIENT: (id: string) => `prescriptions-table-cell-patient-${id}`,
        DOCTOR: (id: string) => `prescriptions-table-cell-doctor-${id}`,
        DATE: (id: string) => `prescriptions-table-cell-date-${id}`,
        STATUS: (id: string) => `prescriptions-table-cell-status-${id}`,
        ACTIONS: (id: string) => `prescriptions-table-cell-actions-${id}`,
      }
    },
    ADD_BUTTON: 'prescriptions-add-button',
    NO_RESULTS: 'prescriptions-no-results'
  },
  FORM: {
    CONTAINER: 'prescription-form-container',
    PATIENT_SEARCH: 'prescription-form-patient-search',
    DOCTOR_SEARCH: 'prescription-form-doctor-search',
    HOSPITAL_SEARCH: 'prescription-form-hospital-search',
    
    MEDICINES: {
      CONTAINER: 'prescription-form-medicines-container',
      MEDICINE_SEARCH: 'prescription-form-medicine-search',
      MEDICINE: (index: number) => `prescription-form-medicine-${index}`,
      DOSAGE: (index: number) => `prescription-form-medicine-dosage-${index}`,
      FREQUENCY: (index: number) => `prescription-form-medicine-frequency-${index}`,
      DURATION: (index: number) => `prescription-form-medicine-duration-${index}`,
      REMOVE_BUTTON: (index: number) => `prescription-form-medicine-remove-${index}`,
    },
    
    ADD_MEDICINE_BUTTON: 'prescription-form-add-medicine-button',
    
    REFILL_SECTION: {
      CONTAINER: 'prescription-form-refill-section',
      REFILLS_AUTHORIZED: 'prescription-form-refills-authorized',
      DAYS_BETWEEN_REFILLS: 'prescription-form-days-between-refills',
      REFILL_VALIDITY: 'prescription-form-refill-validity',
    },
    
    NOTES: 'prescription-form-notes',
    SUBMIT_BUTTON: 'prescription-form-submit-button',
    CANCEL_BUTTON: 'prescription-form-cancel-button',
  },
  DETAILS: {
    CONTAINER: (id: string) => `prescription-details-container-${id}`,
    HEADER: {
      CONTAINER: 'prescription-details-header',
      ID: 'prescription-details-id',
      STATUS: 'prescription-details-status',
      DATE: 'prescription-details-date',
    },
    INFO_SECTION: {
      CONTAINER: 'prescription-details-info-section',
      PATIENT: 'prescription-details-patient',
      DOCTOR: 'prescription-details-doctor',
      HOSPITAL: 'prescription-details-hospital',
    },
    MEDICINES: {
      CONTAINER: 'prescription-details-medicines',
      MEDICINE: (index: number) => `prescription-details-medicine-${index}`,
    },
    REFILL_INFO: {
      CONTAINER: 'prescription-details-refill-info',
      AUTHORIZED: 'prescription-details-refills-authorized',
      REMAINING: 'prescription-details-refills-remaining',
      NEXT_DATE: 'prescription-details-next-refill-date',
      VALID_UNTIL: 'prescription-details-valid-until',
    },
    NOTES: 'prescription-details-notes',
    ACTIONS: {
      CONTAINER: 'prescription-details-actions',
      APPROVE: 'prescription-details-approve-button',
      REJECT: 'prescription-details-reject-button',
      EDIT: 'prescription-details-edit-button',
      PRINT: 'prescription-details-print-button',
      REFILL: 'prescription-details-refill-button',
    }
  }
};

/** Users management components */
export const USERS = {
  CONTAINER: 'users-container',
  LIST: {
    CONTAINER: 'users-list-container',
    SEARCH: 'users-search-input',
    TABLE: {
      CONTAINER: 'users-table-container',
      HEADER_ROW: 'users-table-header-row',
      ROW: (id: string) => `users-table-row-${id}`,
      CELL: {
        NAME: (id: string) => `users-table-cell-name-${id}`,
        EMAIL: (id: string) => `users-table-cell-email-${id}`,
        ROLE: (id: string) => `users-table-cell-role-${id}`,
        CREATED: (id: string) => `users-table-cell-created-${id}`,
        ACTIONS: (id: string) => `users-table-cell-actions-${id}`,
      }
    },
    CARDS: {
      CONTAINER: 'users-cards-container',
      CARD: (id: string) => `user-card-${id}`,
      NAME: (id: string) => `user-card-name-${id}`,
      EMAIL: (id: string) => `user-card-email-${id}`,
      ROLE: (id: string) => `user-card-role-${id}`,
      ACTIONS: {
        MENU: (id: string) => `user-card-actions-menu-${id}`,
        CHANGE_ROLE: (id: string) => `user-card-change-role-${id}`,
        DELETE: (id: string) => `user-card-delete-${id}`,
      }
    },
    NO_RESULTS: 'users-no-results'
  },
  DIALOGS: {
    DELETE: {
      CONTAINER: 'user-delete-dialog',
      CONFIRM: 'user-delete-confirm-button',
      CANCEL: 'user-delete-cancel-button',
    },
    ROLE: {
      CONTAINER: 'user-role-dialog',
      SELECT: 'user-role-select',
      CONFIRM: 'user-role-confirm-button',
      CANCEL: 'user-role-cancel-button',
    }
  }
};

/** Profile page components */
export const PROFILE = {
  CONTAINER: 'profile-container',
  BACK_BUTTON: 'profile-back-button',
  USER_INFO: {
    CONTAINER: 'user-info-container',
    NAME: 'user-info-name',
    EMAIL: 'user-info-email',
    ROLE: 'user-info-role',
    POSITION: 'user-info-position',
    DEPARTMENT: 'user-info-department',
    PHARMACY: 'user-info-pharmacy'
  },
  PIN_SETTINGS: {
    CONTAINER: 'pin-settings-container',
    RESET_BUTTON: 'pin-reset-button',
    SETUP_FORM: 'pin-setup-form',
    PIN_INPUT: 'pin-input',
    CONFIRM_PIN_INPUT: 'confirm-pin-input',
    SUBMIT_BUTTON: 'pin-submit-button',
    ERROR_MESSAGE: 'pin-error-message',
    SUCCESS_MESSAGE: 'pin-success-message'
  }
};

export default {
  COMMON,
  AUTH,
  DASHBOARD,
  SALES,
  PATIENTS,
  INVENTORY,
  PHARMACY,
  EMPLOYEES,
  REPORTS,
  SETTINGS,
  PRESCRIPTIONS,
  USERS,
  PROFILE
};
